import React from 'react'; // Add this import
import { createStyles, Theme } from '@material-ui/core/styles';
import { RENDER_STATE } from 'react-map-gl-draw';
import { renderToString } from 'react-dom/server';
import svgToMiniDataURI from 'mini-svg-data-uri';
import { InfoControl } from '@marapp/earth-shared';

export function getEditHandleStyle({ feature, state }) {
  switch (state) {
    case RENDER_STATE.SELECTED:
    case RENDER_STATE.HOVERED:
    case RENDER_STATE.UNCOMMITTED:
      return {
        fill: 'rgb(251, 176, 59)',
        fillOpacity: 1,
        stroke: 'rgb(255, 255, 255)',
        strokeWidth: 2,
        r: 7,
      };

    default:
      return {
        fill: 'rgb(251, 176, 59)',
        fillOpacity: 1,
        stroke: 'rgb(255, 255, 255)',
        strokeWidth: 2,
        r: 5,
      };
  }
}

export function getFeatureStyle({ feature, index, state }) {
  switch (state) {
    case RENDER_STATE.SELECTED:
    case RENDER_STATE.HOVERED:
    case RENDER_STATE.UNCOMMITTED:
    case RENDER_STATE.CLOSING:
      return {
        stroke: 'rgb(251, 176, 59)',
        strokeWidth: 2,
        fill: 'rgb(251, 176, 59)',
        fillOpacity: 0.3,
        strokeDasharray: '4,2',
      };

    default:
      return {
        stroke: 'rgb(60, 178, 208)',
        strokeWidth: 2,
        fill: 'rgb(60, 178, 208)',
        fillOpacity: 0.1,
      };
  }
}

export const styles = (theme: Theme) =>
  createStyles({
    root: {
      position: 'absolute',
      height: '100%',
      width: '100%',
      zIndex: 0,
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
      '& .hidden': {
        display: 'none !important',
        visibility: 'hidden',
      },
      '& .mapboxgl-ctrl-parent': {
        width: '100%',
        height: '100%',
      },
      '& .mapboxgl-ctrl-attrib': {
        '&.mapboxgl-compact': {
          position: 'absolute',
          bottom: theme.spacing(16.4),
          right: theme.spacing(1.8),
          backgroundColor: theme.palette.grey['600'],
          borderRadius: 4,
          boxShadow: theme.shadows[2],
          marginLeft: 'auto',
          maxWidth: 400,
          minWidth: 30,
          minHeight: 30,
          fontSize: 'clamp(10px, 1vw, 12px)',
          '&:hover': {
            backgroundColor: theme.palette.grey['500'],
          },
          '&.mapboxgl-compact-show': {
            backgroundColor: 'hsla(0,0%,100%,.5)',
            '& .mapboxgl-ctrl-attrib-button': {
              width: 30,
              height: 30,
            },
          },
          '& .mapboxgl-ctrl-attrib-button': {
            top: 'auto',
            backgroundImage: 'none',
            backgroundColor: 'transparent',
            maskImage: `url("${svgToMiniDataURI(renderToString(<InfoControl />))}")`,
            maskSize: '20px 20px',
            maskRepeat: 'no-repeat',
            maskPosition: 'center',
            WebkitMaskImage: `url("${svgToMiniDataURI(renderToString(<InfoControl />))}")`,
            WebkitMaskSize: '20px 20px',
            WebkitMaskRepeat: 'no-repeat',
            WebkitMaskPosition: 'center',
            bottom: 0,
            width: 30,
            height: 30,
            '&:hover': {
              boxShadow: 'none',
            },
          },
        },
      },
      '& .mapboxgl-ctrl-attrib-inner': {
        textAlign: 'right',
        color: theme.palette.grey['800'],
        '&:hover': {
          color: theme.palette.grey['900'],
        },
        '& a[title="Improve this map"]': {
          display: 'none',
        },
      },
      '& .marapp-qa-io-attribution': {
        position: 'absolute',
        right: 0,
        bottom: 0,
        height: 20,
        width: 'fit-content !important',
        padding: '2px 108px 2px 6px',
        color: theme.palette.grey['800'],
        fontSize: '12px',
        backgroundColor: 'hsla(0,0%,100%,.5)',
        '& a': {
          borderBottom: `1px solid ${theme.palette.secondary.main}`,
          '&:hover': {
            borderBottomColor: 'transparent',
          },
        },
      },
      '& .mapboxgl-ctrl-scale': {
        position: 'absolute',
        right: theme.spacing(3),
        bottom: 2,
        fontSize: 11,
        lineHeight: '14px',
        minWidth: 54,
        padding: '0 3px',
        zIndex: 10,
        backgroundColor: 'transparent',
        borderColor: theme.palette.grey['900'],
        color: theme.palette.grey['900'],
      },
      '& .custom-draw-tools': {
        position: 'absolute',
        top: theme.spacing(1.75),
        right: theme.spacing(3.5),
        zIndex: 1000,
      },
      '& .control-panel': {
        position: 'absolute',
        top: theme.spacing(2),
        right: theme.spacing(8),
        maxWidth: '200px',
        outline: 'none',
      },
    },
    open: {
      width: '100%',
    },
    spinner: {
      display: 'inline-block !important',
      maxWidth: '100%',
      float: 'left',
      position: 'static',
      padding: 0,
      marginRight: 8,
    },
    deleteButton: {
      backgroundColor: '#1e282e',
      color: '#d3d3d3',
      border: '1px solid #354752',
      borderRadius: '4px',
      width: '32px',
      height: '32px',
      boxShadow: 'none',
      '&:hover': {
        backgroundColor: '#354752',
        color: '#f0f3f6',
        boxShadow: 'none',
      },
    },
    sourcesPopup: {
      position: 'absolute', // 'fixed',
      bottom: 28, // 28,
      left: 0, // right: 0,
      backgroundColor: 'hsla(0,0%,100%,.5)',
      padding: '2px 6px',
      zIndex: 99999,
      width: 'fit-content', // Use fit-content for auto width, or set a specific value
      maxWidth: 400, // Maximum width constraint
      minWidth: 200, // Optional: minimum width if needed
      fontSize: '12px',
      color: theme.palette.grey['800'],
      '& a': {
        borderBottom: `1px solid ${theme.palette.secondary.main}`,
        '&:hover': {
          borderBottomColor: 'transparent',
        },
      },
    },
    sourcesPopupHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 4,
      fontWeight: 'bold',
      fontSize: '12px',
      color: theme.palette.grey['800'],
    },
    sourcesPopupClose: {
      cursor: 'pointer',
      padding: 4,
      marginLeft: 12,
      '&:hover': {
        opacity: 0.7,
      },
    },
  });
